class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.quoteOpen = false
    this.quoteSuccess = false
    this.quoteSuccessTimer = null
    this.quoteCloseTimer = null
    this.quoteCloseTransitionHandler = null

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return
      if (this.isQuoteOpen()) {
        // After a successful quote, dismiss quote + cart together.
        if (this.quoteSuccess) {
          this.close()
          return
        }
        this.closeQuote()
        return
      }
      this.close()
    })
    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this)
    )
    this.addEventListener('click', this.onQuoteClick.bind(this))
    this.addEventListener('change', (event) => {
      this.onAgreeToConditionsChange(event)
      this.onBillingSameChange(event)
      this.onQuoteCountryChange(event)
    })
    this.querySelector('[data-cart-quote-form]')?.addEventListener(
      'submit',
      this.onQuoteSubmit.bind(this)
    )
    this.initQuoteCountries()
    this.syncBillingSameState()
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (this.isQuoteOpen()) this.trapQuoteFocus()
      })
    }
    this.setHeaderCartIconAccessibility()
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', (event) => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote)
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active')
    })

    this.addEventListener(
      'transitionend',
      () => {
        if (this.isQuoteOpen()) {
          this.trapQuoteFocus()
          return
        }
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    if (this.isQuoteOpen())
      this.closeQuote({ restoreFocus: false, instant: true })
    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      )
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      )
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty')
    this.productId = parsedState.id
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id)
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      )
    })

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this)
      )
      this.open()
    })
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }

  isQuoteOpen() {
    return this.quoteOpen
  }

  quotePanel() {
    return this.querySelector('#CartDrawer-Quote')
  }

  onAgreeToConditionsChange(event) {
    if (event.target.id !== 'CartDrawer-AgreeToConditions') return
    const checkout = this.querySelector('#CartDrawer-Checkout')
    if (!checkout) return
    if (event.target.checked && !this.classList.contains('is-empty')) {
      checkout.removeAttribute('disabled')
    } else {
      checkout.setAttribute('disabled', '')
    }
  }

  onBillingSameChange(event) {
    if (!event.target.matches('[data-cart-quote-billing-same]')) return
    this.syncBillingSameState()
  }

  syncBillingSameState() {
    const checkbox = this.querySelector('[data-cart-quote-billing-same]')
    const billing = this.querySelector('[data-cart-quote-billing]')
    if (!checkbox || !billing) return

    const sameAsShipping = checkbox.checked
    billing.hidden = sameAsShipping
    this.querySelectorAll('[data-cart-quote-billing-required]').forEach((input) => {
      if (sameAsShipping) {
        input.removeAttribute('required')
      } else {
        input.setAttribute('required', '')
      }
    })
    this.syncQuoteProvince('billing')
  }

  initQuoteCountries() {
    this.querySelectorAll('[data-cart-quote-country]').forEach((select) => {
      const preferred = select.dataset.default
      const hasPreferred = [...select.options].some(
        (option) => option.value === preferred
      )
      select.value = hasPreferred ? preferred : 'United States'

      const prefix = select.dataset.cartQuoteCountry
      this.syncQuoteProvince(prefix)
      const province = this.querySelector(`[name="${prefix}_province"]`)
      const preferredProvince = province?.dataset.default
      if (
        preferredProvince &&
        [...province.options].some((option) => option.value === preferredProvince)
      ) {
        province.value = preferredProvince
      }
    })
  }

  onQuoteCountryChange(event) {
    const prefix = event.target.dataset?.cartQuoteCountry
    if (prefix) this.syncQuoteProvince(prefix)
  }

  /**
   * Rebuilds the state/province list for the chosen country. Countries without
   * provinces hide the field so it isn't required.
   */
  syncQuoteProvince(prefix) {
    const country = this.querySelector(`[data-cart-quote-country="${prefix}"]`)
    const province = this.querySelector(`[name="${prefix}_province"]`)
    const field = this.querySelector(`[data-cart-quote-province-field="${prefix}"]`)
    if (!country || !province || !field) return

    const option = country.options[country.selectedIndex]
    let provinces = []
    try {
      provinces = JSON.parse(option?.dataset.provinces || '[]')
    } catch {
      provinces = []
    }

    if (province.dataset.country !== country.value) {
      province.dataset.country = country.value
      const prompt = field.querySelector('label')?.textContent.trim() || ''
      province.replaceChildren(
        ...(provinces.length ? [new Option(prompt, '')] : []),
        ...provinces.map(([value, label]) => new Option(label, value))
      )
    }

    const hasProvinces = provinces.length > 0
    field.hidden = !hasProvinces
    const billingSame =
      prefix === 'billing' &&
      this.querySelector('[data-cart-quote-billing-same]')?.checked
    province.required = hasProvinces && !billingSame
  }

  onQuoteClick(event) {
    if (event.target.closest('[data-cart-quote-open]')) {
      event.preventDefault()
      this.openQuote()
      return
    }
    if (event.target.closest('[data-cart-quote-close]')) {
      event.preventDefault()
      // Success: one close dismisses quote + cart. Form: back to cart only.
      if (this.quoteSuccess) {
        this.close()
        return
      }
      this.closeQuote()
    }
  }

  openQuote() {
    const panel = this.quotePanel()
    if (!panel) return

    this.cancelQuoteCloseAnimation()
    this.clearQuoteSuccessTimer()
    this.quoteOpen = true
    this.quoteSuccess = false
    this.setQuoteSuccess(false)
    this.setQuoteError('')

    this.classList.remove('quote-open')
    panel.hidden = false

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.quoteOpen) return
        this.classList.add('quote-open')
        this.querySelector('#CartDrawer')?.setAttribute('aria-hidden', 'true')
        this.trapQuoteFocus()
        this.ensureTurnstileWidget()
      })
    })
  }

  cancelQuoteCloseAnimation() {
    if (this.quoteCloseTimer) {
      clearTimeout(this.quoteCloseTimer)
      this.quoteCloseTimer = null
    }
    const panel = this.quotePanel()
    if (this.quoteCloseTransitionHandler && panel) {
      panel.removeEventListener(
        'transitionend',
        this.quoteCloseTransitionHandler
      )
      this.quoteCloseTransitionHandler = null
    }
  }

  quoteTransitionDuration() {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--duration-default')
      .trim()
    const ms = parseInt(raw, 10)
    return Number.isFinite(ms) ? ms : 200
  }

  closeQuote({ restoreFocus = true, instant = false } = {}) {
    const panel = this.quotePanel()
    if (!panel || !this.quoteOpen) return

    this.cancelQuoteCloseAnimation()
    this.clearQuoteSuccessTimer()
    this.quoteOpen = false
    this.quoteSuccess = false
    this.classList.remove('quote-open')
    this.querySelector('#CartDrawer')?.removeAttribute('aria-hidden')
    this.setQuoteSubmitting(false)

    const finishClose = () => {
      panel.hidden = true
      this.setQuoteSuccess(false)
      this.setQuoteError('')
      this.cancelQuoteCloseAnimation()

      if (!restoreFocus || !this.classList.contains('active')) return

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer')
      const focusElement =
        this.querySelector('[data-cart-quote-open]') ||
        this.querySelector('.drawer__inner') ||
        this.querySelector('.drawer__close')
      if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement)
    }

    if (instant) {
      finishClose()
      return
    }

    let finished = false
    const complete = () => {
      if (finished) return
      finished = true
      finishClose()
    }

    this.quoteCloseTransitionHandler = (event) => {
      if (event.target !== panel || event.propertyName !== 'transform') return
      complete()
    }
    panel.addEventListener('transitionend', this.quoteCloseTransitionHandler)
    this.quoteCloseTimer = setTimeout(
      complete,
      this.quoteTransitionDuration() + 50
    )
  }

  trapQuoteFocus() {
    const panel = this.quotePanel()
    if (!panel) return
    const focusElement = panel.querySelector('#CartDrawer-QuoteEmail') || panel
    trapFocus(panel, focusElement)
  }

  setQuoteSuccess(isSuccess) {
    this.quoteSuccess = isSuccess
    const form = this.querySelector('[data-cart-quote-form]')
    const success = this.querySelector('[data-cart-quote-success]')
    if (form) form.hidden = isSuccess
    if (success) success.hidden = !isSuccess
    if (isSuccess) {
      this.scheduleQuoteSuccessDismiss()
    } else {
      this.clearQuoteSuccessTimer()
    }
  }

  quoteSuccessDuration() {
    const seconds = parseInt(this.dataset.quoteSuccessDuration, 10)
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : 5
  }

  clearQuoteSuccessTimer() {
    if (!this.quoteSuccessTimer) return
    clearTimeout(this.quoteSuccessTimer)
    this.quoteSuccessTimer = null
  }

  scheduleQuoteSuccessDismiss() {
    this.clearQuoteSuccessTimer()
    const seconds = this.quoteSuccessDuration()
    if (seconds === 0) return

    this.quoteSuccessTimer = setTimeout(() => {
      this.quoteSuccessTimer = null
      if (this.isQuoteOpen() && this.quoteSuccess) this.closeQuote()
    }, seconds * 1000)
  }

  setQuoteError(message) {
    const error = this.querySelector('#CartDrawer-QuoteError')
    if (!error) return
    error.hidden = !message
    error.textContent = message || ''
  }

  setQuoteSubmitting(isSubmitting) {
    const submit = this.querySelector('[data-cart-quote-submit]')
    if (!submit) return
    submit.disabled = isSubmitting
    submit.classList.toggle('loading', isSubmitting)
    submit.setAttribute('aria-busy', String(isSubmitting))
    submit
      .querySelector('.loading-overlay__spinner')
      ?.classList.toggle('hidden', !isSubmitting)
  }

  /**
   * Stopwatch for the quote submit steps. Logs to the console at "Verbose" level
   * so it stays out of the way unless someone is looking for it.
   */
  createQuoteTimer() {
    const start = performance.now()
    let last = start
    const steps = {}
    return {
      lap(name) {
        const now = performance.now()
        steps[name] = Math.round(now - last)
        last = now
      },
      log(response) {
        steps.total = Math.round(performance.now() - start)
        const worker = response?.headers?.get('Server-Timing') || 'n/a'
        console.debug('[cart quote] timing (ms)', steps, `worker: ${worker}`)
      }
    }
  }

  loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve()
    if (this._turnstileScriptPromise) return this._turnstileScriptPromise

    this._turnstileScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-cart-quote-turnstile-api]'
      )
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener(
          'error',
          () => reject(new Error('turnstile-script')),
          { once: true }
        )
        if (window.turnstile) resolve()
        return
      }

      const script = document.createElement('script')
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.dataset.cartQuoteTurnstileApi = 'true'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('turnstile-script'))
      document.head.appendChild(script)
    })

    return this._turnstileScriptPromise
  }

  async ensureTurnstileWidget() {
    const sitekey = this.dataset.turnstileSitekey
    const el = this.querySelector('[data-cart-quote-turnstile]')
    if (!sitekey || !el) return false

    try {
      // Wait for script onload — do not call turnstile.ready() with async api.js
      // (Cloudflare throws: Remove async/defer before using turnstile.ready()).
      await this.loadTurnstileScript()
    } catch {
      return false
    }

    if (!window.turnstile?.render) return false

    if (this._turnstileWidgetEl !== el) {
      this._turnstileWidgetId = null
      this._turnstileWidgetEl = el
      this._turnstileToken = ''
    }

    if (this._turnstileWidgetId != null) return true

    try {
      this._turnstileToken = ''
      this._turnstileWidgetId = window.turnstile.render(el, {
        sitekey,
        action: 'cart_quote',
        theme: 'light',
        // Always show the widget so shoppers aren't told to "complete verification"
        // when managed mode auto-passes with an empty/invisible UI.
        appearance: 'always',
        callback: (token) => {
          this._turnstileToken = typeof token === 'string' ? token : ''
        },
        'expired-callback': () => {
          this._turnstileToken = ''
        },
        'error-callback': () => {
          this._turnstileToken = ''
        }
      })
      return true
    } catch {
      return false
    }
  }

  /**
   * Returns a Turnstile token, waiting briefly if the challenge is still solving.
   * Preview hosts especially can take a few seconds after open before a token exists.
   */
  async getTurnstileToken() {
    const ready = await this.ensureTurnstileWidget()
    if (!ready || this._turnstileWidgetId == null || !window.turnstile) {
      return ''
    }

    const readToken = () => {
      if (this._turnstileToken) return this._turnstileToken
      try {
        return window.turnstile.getResponse(this._turnstileWidgetId) || ''
      } catch {
        return ''
      }
    }

    let token = readToken()
    if (token) return token

    const deadline = Date.now() + 8000
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 250))
      token = readToken()
      if (token) return token
    }
    return ''
  }

  resetTurnstileWidget() {
    this._turnstileToken = ''
    if (
      this._turnstileWidgetId == null ||
      !window.turnstile?.reset
    ) {
      return
    }
    try {
      window.turnstile.reset(this._turnstileWidgetId)
    } catch {
      // Widget may have been removed by a cart section re-render.
      this._turnstileWidgetId = null
      this._turnstileWidgetEl = null
    }
  }

  async onQuoteSubmit(event) {
    event.preventDefault()
    const form = event.target
    const emailInput = this.querySelector('#CartDrawer-QuoteEmail')
    const email = emailInput?.value?.trim() || ''
    const webhook = this.dataset.quoteWebhook
    const turnstileSitekey = this.dataset.turnstileSitekey

    this.setQuoteError('')
    this.syncBillingSameState()

    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    if (!email || !emailInput.checkValidity()) {
      emailInput?.reportValidity()
      return
    }

    if (!webhook || !turnstileSitekey) {
      this.setQuoteError(this.quoteErrorMessage())
      return
    }

    const shippingAddress = this.readQuoteAddress('shipping')
    if (!shippingAddress) {
      form.reportValidity()
      return
    }

    const billingSame = this.querySelector('[data-cart-quote-billing-same]')?.checked
    const billingAddress = billingSame
      ? { ...shippingAddress }
      : this.readQuoteAddress('billing')

    if (!billingAddress) {
      form.reportValidity()
      return
    }

    this.setQuoteSubmitting(true)
    const timer = this.createQuoteTimer()
    let quoteResponse

    try {
      const turnstileToken = await this.getTurnstileToken()
      timer.lap('turnstile_wait')
      if (!turnstileToken) {
        this.setQuoteError(this.quoteTurnstileErrorMessage())
        this.setQuoteSubmitting(false)
        return
      }

      let cart = this.readQuoteCart()
      if (!cart) {
        const cartUrl = window.routes?.cart_url || '/cart'
        const cartResponse = await fetch(`${cartUrl}.js`)
        if (!cartResponse.ok) throw new Error('cart')
        cart = await cartResponse.json()
      }
      timer.lap('cart')
      const lineItems = this.quotableLineItems(cart)

      if (!lineItems.length) {
        this.setQuoteError(this.quoteEmptyMessage())
        this.setQuoteSubmitting(false)
        this.resetTurnstileWidget()
        return
      }

      quoteResponse = await fetch(webhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          line_items: lineItems,
          shipping_address: shippingAddress,
          billing_address: billingAddress,
          turnstile_token: turnstileToken
        })
      })
      timer.lap('worker_round_trip')

      if (quoteResponse.status === 429) {
        this.setQuoteError(this.quoteRateLimitErrorMessage())
        this.resetTurnstileWidget()
        return
      }

      if (quoteResponse.status === 403) {
        this.setQuoteError(this.quoteTurnstileErrorMessage())
        this.resetTurnstileWidget()
        return
      }

      if (!quoteResponse.ok && quoteResponse.status !== 204) {
        throw new Error('quote')
      }

      if (window.Shopify?.analytics?.publish) {
        Shopify.analytics.publish('quote_request_submitted', {
          value: (cart.total_price || 0) / 100,
          currency: cart.currency || window.Shopify?.currency?.active || 'USD'
        })
      }

      this.setQuoteSuccess(true)
      this.resetTurnstileWidget()
      const panel = this.quotePanel()
      const successClose = panel?.querySelector('.drawer__close')
      if (panel) trapFocus(panel, successClose || panel)
    } catch {
      this.setQuoteError(this.quoteErrorMessage())
      this.resetTurnstileWidget()
    } finally {
      this.setQuoteSubmitting(false)
      timer.log(quoteResponse)
    }
  }

  readQuoteAddress(prefix) {
    const requiredKeys = [
      'first_name',
      'last_name',
      'address1',
      'city',
      'country',
      'zip'
    ]
    const province = this.querySelector(`[name="${prefix}_province"]`)
    if (province?.options.length > 1) requiredKeys.push('province')
    const optionalKeys = ['company', 'address2', 'phone']
    const address = {}

    for (const key of requiredKeys) {
      const input = this.querySelector(`[name="${prefix}_${key}"]`)
      const value = input?.value?.trim() || ''
      if (!value) return null
      address[key] = value
    }

    for (const key of optionalKeys) {
      const input = this.querySelector(`[name="${prefix}_${key}"]`)
      const value = input?.value?.trim() || ''
      if (value) address[key] = value
    }

    return address
  }

  /**
   * Reads the cart snapshot rendered into the drawer, so submit can skip a /cart.js round trip.
   * It lives inside #CartDrawer-CartItems, which every cart update re-renders.
   * Returns null when missing or unparseable; the caller falls back to /cart.js.
   */
  readQuoteCart() {
    const el = this.querySelector('[data-cart-quote-data]')
    if (!el) return null
    try {
      const cart = JSON.parse(el.textContent)
      return Array.isArray(cart?.items) ? cart : null
    } catch {
      return null
    }
  }

  quotableLineItems(cart) {
    const excluded = new Set(
      [...this.querySelectorAll('[data-quote-excluded]')].map((el) =>
        String(el.dataset.variantId)
      )
    )

    return (cart.items || [])
      .filter((item) => !excluded.has(String(item.variant_id)))
      .map((item) => {
        const properties = {}
        if (item.properties) {
          Object.keys(item.properties).forEach((key) => {
            if (
              key.charAt(0) === '_' ||
              item.properties[key] == null ||
              item.properties[key] === ''
            ) {
              return
            }
            properties[key] = item.properties[key]
          })
        }
        const line = {
          variant_id: item.variant_id,
          quantity: item.quantity
        }
        if (Object.keys(properties).length) line.properties = properties
        return line
      })
  }

  quoteErrorMessage() {
    return (
      window.cartStrings?.quoteError ||
      "We couldn't send your quote request. Please try again."
    )
  }

  quoteEmptyMessage() {
    return (
      window.cartStrings?.quoteEmptyError ||
      'There are no quote-eligible items in your cart.'
    )
  }

  quoteTurnstileErrorMessage() {
    return (
      window.cartStrings?.quoteTurnstileError ||
      'Please complete the verification check and try again.'
    )
  }

  quoteRateLimitErrorMessage() {
    return (
      window.cartStrings?.quoteRateLimitError ||
      'Too many quote requests. Please wait a minute and try again.'
    )
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const source = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('#CartDrawer-CartItems')

        const target = document.getElementById('CartDrawer-CartItems')
        if (source && target) target.innerHTML = source.innerHTML
      })
      .catch((e) => {
        console.error(e)
      })
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ]
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
